import { ShortcutCard } from "~/components/ShortcutCard"
import { KeyboardIcon } from "lucide-react"
import { type ReactNode, useEffect, useRef, useState } from "react"
import { SHORTCUTS } from "~/components/editor/shortcuts"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs"
import {
	appShortcuts,
	displayShortcut,
	isModifiedShortcut,
	modifierKey,
	quickNavigationShortcuts
} from "~/lib/keyboard-shortcuts"
import { Kbd, KbdGroup } from "./ui/kbd"

const shortcutGroups = [
	{
		title: "Global Shortcuts",
		shortcuts: [
			...Object.values(appShortcuts),
			{
				title: "Create Issue",
				description: "Open the create issue dialog.",
				keys: quickNavigationShortcuts.create_issue.keys
			}
		]
	},
	{
		title: "Editor Formatting Shortcuts",
		shortcuts: [
			{
				title: "Normal Text",
				description: "Format the current block as paragraph text.",
				shortcut: SHORTCUTS.NORMAL
			},
			{
				title: "Heading 1",
				description: "Format the current block as a heading.",
				shortcut: SHORTCUTS.HEADING1
			},
			{
				title: "Heading 2",
				description: "Format the current block as a heading.",
				shortcut: SHORTCUTS.HEADING2
			},
			{
				title: "Heading 3",
				description: "Format the current block as a heading.",
				shortcut: SHORTCUTS.HEADING3
			},
			{
				title: "Bullet List",
				description: "Format the current block as a bullet list.",
				shortcut: SHORTCUTS.BULLET_LIST
			},
			{
				title: "Numbered List",
				description: "Format the current block as a numbered list.",
				shortcut: SHORTCUTS.NUMBERED_LIST
			},
			{
				title: "Code Block",
				description: "Format the current block as code.",
				shortcut: SHORTCUTS.CODE_BLOCK
			},
			{
				title: "Bold",
				description: "Toggle bold formatting.",
				shortcut: SHORTCUTS.BOLD
			},
			{
				title: "Italic",
				description: "Toggle italic formatting.",
				shortcut: SHORTCUTS.ITALIC
			},
			{
				title: "Underline",
				description: "Toggle underline formatting.",
				shortcut: SHORTCUTS.UNDERLINE
			},
			{
				title: "Strikethrough",
				description: "Toggle strikethrough formatting.",
				shortcut: SHORTCUTS.STRIKETHROUGH
			}
		]
	}
]

const quickNavigationGroups = [
	{
		title: "Me",
		shortcuts: [
			quickNavigationShortcuts.go_home,
			quickNavigationShortcuts.open_inbox,
			quickNavigationShortcuts.open_my_issues,
			quickNavigationShortcuts.open_user_settings
		]
	},
	{
		title: "Organization",
		shortcuts: [quickNavigationShortcuts.open_organization_settings]
	},
	{
		title: "Team",
		shortcuts: [
			quickNavigationShortcuts.open_team_issues,
			quickNavigationShortcuts.open_team_queue,
			quickNavigationShortcuts.open_knowledge_base,
			quickNavigationShortcuts.open_team_settings
		]
	},
	{
		title: "Create",
		shortcuts: [
			quickNavigationShortcuts.create_issue,
			quickNavigationShortcuts.create_knowledge_base_page
		]
	}
]

const konamiCode = [
	"ArrowUp",
	"ArrowUp",
	"ArrowDown",
	"ArrowDown",
	"ArrowLeft",
	"ArrowRight",
	"ArrowLeft",
	"ArrowRight"
] as const

export function KeyboardShortcutsDialog({
	children
}: {
	children?: ReactNode
}) {
	const [open, setOpen] = useState(false)
	const konamiIndex = useRef(0)

	useEffect(() => {
		const down = (event: KeyboardEvent) => {
			if (
				isModifiedShortcut(event, appShortcuts.open_keyboard_shortcuts.keys)
			) {
				event.preventDefault()
				setOpen((open) => !open)
			}
		}

		document.addEventListener("keydown", down)
		return () => document.removeEventListener("keydown", down)
	}, [])

	useEffect(() => {
		// What is an app with no easter eggs 🔥
		if (!open) {
			konamiIndex.current = 0
			return
		}

		const down = (event: KeyboardEvent) => {
			if (!konamiCode.includes(event.key as (typeof konamiCode)[number])) {
				konamiIndex.current = 0
				return
			}

			const expectedKey = konamiCode[konamiIndex.current]
			konamiIndex.current =
				event.key === expectedKey ? konamiIndex.current + 1 : 0

			if (konamiIndex.current === konamiCode.length) {
				konamiIndex.current = 0
				const audio = new Audio("/orbit_glaze.mp3")
				audio.play()
			}
		}

		document.addEventListener("keydown", down)
		return () => document.removeEventListener("keydown", down)
	}, [open])

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			{children && <DialogTrigger asChild>{children}</DialogTrigger>}
			<DialogContent
				className="grid max-h-[min(760px,calc(100svh-2rem))] border-stroke bg-background-secondary"
				size="lg"
				layout="scroll"
			>
				<DialogHeader variant="divided">
					<div className="flex items-start justify-between gap-4">
						<div className="min-w-0 space-y-1">
							<div className="flex items-center gap-2">
								<KeyboardIcon className="size-5 text-icons" />
								<DialogTitle className="text-xl">
									Keyboard Shortcuts
								</DialogTitle>
								<div className="flex items-center gap-1">
									<KbdGroup>
										<Kbd>{modifierKey}</Kbd>
										<Kbd>/</Kbd>
									</KbdGroup>
								</div>
							</div>
						</div>
					</div>
				</DialogHeader>
				<Tabs
					defaultValue="shortcuts"
					className="min-h-0 gap-0 overflow-hidden px-5 py-4 sm:px-7"
				>
					<TabsList className="mb-4 grid grid-cols-2" stretch>
						<TabsTrigger value="shortcuts">Shortcuts</TabsTrigger>
						<TabsTrigger value="quick-navigation">Quick Navigation</TabsTrigger>
					</TabsList>
					<TabsContent
						value="shortcuts"
						className="min-h-0 overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden"
					>
						<div className="space-y-6 pb-2">
							{shortcutGroups.map((group) => (
								<section key={group.title}>
									<h2 className="mb-3 text-sm font-semibold text-text-main">
										{group.title}
									</h2>
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
										{group.shortcuts.map((shortcut) => {
											const displayKeys =
												"shortcut" in shortcut
													? shortcut.shortcut
													: displayShortcut(shortcut.keys)

											return (
												<ShortcutCard
													key={shortcut.title}
													title={shortcut.title}
													description={shortcut.description}
													keys={displayKeys}
												/>
											)
										})}
									</div>
								</section>
							))}
						</div>
					</TabsContent>
					<TabsContent
						value="quick-navigation"
						className="min-h-0 overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden"
					>
						<div className="space-y-6 pb-2">
							{quickNavigationGroups.map((group) => (
								<section key={group.title}>
									<h2 className="mb-3 text-sm font-semibold text-text-main">
										{group.title}
									</h2>
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
										{group.shortcuts.map((shortcut) => (
											<ShortcutCard
												key={shortcut.title}
												title={shortcut.title}
												description={shortcut.description}
												keys={displayShortcut(shortcut.keys)}
												sequence
											/>
										))}
									</div>
								</section>
							))}
						</div>
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	)
}
